import { Button, IconButton, Typography } from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import DEFAULT_MASTERCLASS_IMAGE from "assets/Placeholder/default-masterclass-thumbnail.svg"
import clsx from "clsx"
import { PlaySolid } from "iconoir-react"
import { useState } from "react"
import { mapErrorsV2 } from "utils/helpers"

enum Step {
  PLAY = "play",
  CONFIRM = "confirm",
}

const ConfirmationScreen = ({
  thumbnail,
  masterclassSlug,
  refetchMasterclass,
}: {
  thumbnail: string
  masterclassSlug: string
  refetchMasterclass: () => void
}) => {
  const [step, setStep] = useState<Step>(Step.PLAY)

  const triggerConfirmWatch = useMutation({
    mutationKey: ["confirmFreeWatch", masterclassSlug],
    mutationFn: () =>
      api.masterclasses.watchFreeMasterclass({
        urlParams: { masterclassSlug },
      }),
    onSuccess: () => {
      refetchMasterclass()
    },
    onError: e => {
      mapErrorsV2(e)
    },
  })

  return (
    <div className="relative">
      <div
        className={clsx(
          "absolute inset-0 z-10 flex items-center justify-center rounded-lg p-4",
          {
            "bg-[rgba(0,0,0,0.80)]": step === Step.CONFIRM,
            "bg-[rgba(0,0,0,0.50)]": step === Step.PLAY,
          }
        )}
      >
        <div className="flex flex-col items-center gap-1 text-center">
          {step === Step.PLAY ? (
            <IconButton
              className="size-8 rounded-lg sm:size-20"
              color={null}
              onClick={() => setStep(Step.CONFIRM)}
            >
              <PlaySolid className="!size-8 text-surface sm:!size-20" />
            </IconButton>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-1.25">
                <div className="flex size-[26px] items-center justify-center rounded-lg bg-highlight-600">
                  <PlaySolid className="size-2 text-surface" />
                </div>
                <Typography className="text-surface" variant="subtitle2">
                  Use your one free view now?
                </Typography>
              </div>

              <Typography className="hidden max-w-[550px] text-surface sm:block">
                You get 1 free Masterclass view each month. The video will be
                available for 7 days after you start watching.
              </Typography>

              <Button
                className="mt-1 bg-surface-50"
                color={null}
                disabled={triggerConfirmWatch.isPending}
                onClick={() => triggerConfirmWatch.mutate()}
              >
                <Typography className="text-onSurface-900" variant="strong">
                  Continue Watching
                </Typography>
              </Button>
            </>
          )}
        </div>
      </div>

      <img
        alt="thumbnail"
        className="w-full rounded-lg"
        src={thumbnail}
        onError={e => (e.currentTarget.src = DEFAULT_MASTERCLASS_IMAGE)}
      />
    </div>
  )
}

export default ConfirmationScreen
